import { useState, useEffect } from 'react';
import { Check, Flame, Eye, Timer, HelpCircle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { BunsenBurner } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Increasing the Flame',
    content: 'Now that excess acid has evaporated, increase to a strong flame. High temperature is needed to convert the hydrated tin oxide to anhydrous SnO2 and drive off any remaining water or volatile compounds.'
  },
  2: {
    title: 'Heating to Red Hot',
    content: 'The crucible should glow red-orange, indicating temperatures around 700-900 degrees Celsius. Maintain this for about ten minutes to ensure complete conversion to tin(IV) oxide (SnO2). Use the lid slightly ajar for air circulation.'
  },
  3: {
    title: 'Observing Residue Color',
    content: 'The final product color indicates the oxide formed: WHITE or cream indicates SnO2 (tin IV oxide, most common). Blue-black suggests some SnO (tin II oxide). The color helps confirm your product before calculations.'
  }
};

export default function Part6StrongHeating() {
  const [flameIntensity, setFlameIntensity] = useState<'off' | 'low' | 'high'>('off');
  const [heatingTime, setHeatingTime] = useState(0);
  const [crucibleGlowing, setCrucibleGlowing] = useState(false);
  const [residueObserved, setResidueObserved] = useState(false);
  const [residueColor, setResidueColor] = useState('#696969');
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // Heating timer
  useEffect(() => {
    if (flameIntensity === 'high' && heatingTime < 100) {
      const timer = setInterval(() => {
        setHeatingTime(prev => {
          const newTime = Math.min(prev + 1, 100);
          if (newTime > 50) {
            setCrucibleGlowing(true);
          }
          // Final product color - mostly white SnO2
          if (newTime >= 100) {
            setResidueColor('#FFFEF5');
          }
          return newTime;
        });
      }, 50);
      return () => clearInterval(timer);
    }
  }, [flameIntensity, heatingTime]);

  const handleIncreaseFlame = () => {
    if (currentStep === 1) {
      setFlameIntensity('high');
      setCurrentStep(2);
    }
  };

  const handleObserveResidue = () => {
    if (heatingTime >= 100 && currentStep === 3) {
      setResidueObserved(true);
    }
  };

  // Auto advance to step 3 when heating complete
  useEffect(() => {
    if (heatingTime >= 100 && currentStep === 2) {
      setCurrentStep(3);
    }
  }, [heatingTime, currentStep]);

  const completedCount = [flameIntensity === 'high', heatingTime >= 100, residueObserved].filter(Boolean).length;

  // Crucible temperature color
  const getCrucibleColor = () => {
    if (heatingTime < 20) return '#D3D3D3';
    if (heatingTime < 40) return '#FFB366';
    if (heatingTime < 60) return '#FF6B35';
    return '#FF4444';
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <Flame size={16} />
          <h3>Part VI: Strong Heating</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Increase Flame', hint: 'Strong, hot flame', done: flameIntensity === 'high' },
            { id: 2, title: 'Heat to Red Hot', hint: 'Crucible glows red (~10 min)', done: heatingTime >= 100 },
            { id: 3, title: 'Observe Residue Color', hint: 'White = SnO₂', done: residueObserved }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {flameIntensity === 'high' && (
          <div className="heating-stats">
            <div className="stat-row">
              <Timer size={14} />
              <span>Heating Progress:</span>
              <span className="value">{heatingTime}%</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${heatingTime}%` }} />
            </div>
            {crucibleGlowing && (
              <div className="glow-indicator">
                Crucible glowing red-hot
              </div>
            )}
          </div>
        )}
      </div>

      <LabBench
        experimentName="Strong Heating"
        completedCount={completedCount}
        totalSteps={3}
        equipment={['Bunsen burner (high flame)', 'Timer', 'Crucible']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="strong-heating-workspace">
          <div className="equipment-row">
            {/* Retort Stand with Crucible */}
            <div className="ring-stand-assembly">
              <svg viewBox="0 0 140 220" width={140} height={220}>
                {/* Stand base */}
                <rect x="30" y="210" width="80" height="8" fill="#444" rx="2" />
                {/* Vertical rod */}
                <rect x="66" y="40" width="8" height="170" fill="#666" />
                {/* Iron ring */}
                <ellipse cx="70" cy="100" rx="40" ry="10" fill="none" stroke="#888" strokeWidth="4" />
                {/* Clay triangle */}
                <polygon points="40,100 100,100 70,88" fill="#B8860B" stroke="#8B6914" strokeWidth="1" />

                {/* Crucible body */}
                <path
                  d="M45 82 L45 65 Q45 48 70 48 Q95 48 95 65 L95 82 Q95 92 70 92 Q45 92 45 82"
                  fill={getCrucibleColor()}
                  stroke="#888"
                  strokeWidth="2"
                />

                {/* Crucible lid (slightly offset for air) */}
                <ellipse cx="70" cy="45" rx="22" ry="6" fill={getCrucibleColor()} stroke="#888" strokeWidth="1.5" />

                {/* Glow effect */}
                {crucibleGlowing && (
                  <>
                    <ellipse cx="70" cy="70" rx="30" ry="20" fill="rgba(255,100,50,0.3)" />
                    <ellipse cx="70" cy="70" rx="22" ry="15" fill="rgba(255,150,50,0.2)" />
                  </>
                )}

                {/* Product inside (visible through glow) */}
                <ellipse
                  cx="70"
                  cy="75"
                  rx="18"
                  ry="7"
                  fill={residueColor}
                  opacity={heatingTime > 80 ? 1 : 0.5}
                />
              </svg>
            </div>

            {/* Bunsen Burner */}
            <div
              className={`burner-station ${currentStep === 1 ? 'clickable' : ''}`}
              onClick={handleIncreaseFlame}
            >
              <BunsenBurner
                isLit={flameIntensity !== 'off'}
                flameIntensity={flameIntensity === 'high' ? 100 : 0}
              />
              <span className="equipment-label">
                Bunsen Burner {flameIntensity === 'high' ? '(HIGH)' : ''}
              </span>
            </div>
          </div>

          {/* Observe Button */}
          {heatingTime >= 100 && !residueObserved && (
            <button className="observe-btn" onClick={handleObserveResidue}>
              <Eye size={18} />
              Observe Residue Color
            </button>
          )}

          {/* Result Display */}
          {residueObserved && (
            <div className="result-panel">
              <h4>Residue Observation</h4>
              <div className="color-display">
                <div
                  className="color-swatch"
                  style={{ background: residueColor }}
                />
                <div className="color-info">
                  <span className="color-name">White/Cream Powder</span>
                  <span className="formula">Tin(IV) Oxide - SnO₂</span>
                </div>
              </div>
              <p className="note">
                The white color indicates SnO₂ (tin IV oxide), where tin has an
                oxidation state of +4.
              </p>
            </div>
          )}

          <div className="info-box">
            <h4>Product Identification</h4>
            <ul>
              <li><strong>White/Cream:</strong> SnO₂ (most common)</li>
              <li><strong>Blue-black:</strong> SnO</li>
              <li><strong>Red:</strong> Metastable SnO</li>
            </ul>
          </div>
        </div>

        <style>{`
          .strong-heating-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 30px;
            align-items: flex-end;
          }

          .ring-stand-assembly {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .burner-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 12px;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .clickable {
            cursor: pointer;
            animation: pulse 1.5s ease-in-out infinite;
          }

          .clickable:hover {
            background: rgba(255, 90, 54, 0.1);
          }

          @keyframes pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255, 90, 54, 0.4); }
            50% { box-shadow: 0 0 0 8px rgba(255, 90, 54, 0); }
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .heating-stats {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 12px;
          }

          .stat-row {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            margin-bottom: 8px;
          }

          .stat-row .value {
            margin-left: auto;
            font-weight: 600;
            font-family: monospace;
          }

          .progress-bar {
            height: 8px;
            background: var(--border);
            border-radius: 4px;
            overflow: hidden;
          }

          .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #FF8C00, #FF4500);
            transition: width 0.1s;
          }

          .glow-indicator {
            margin-top: 8px;
            padding: 6px 10px;
            background: rgba(255, 68, 68, 0.2);
            border-radius: 4px;
            font-size: 11px;
            color: #FF4444;
            text-align: center;
          }

          .observe-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 24px;
            background: var(--secondary);
            color: white;
            border: none;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .observe-btn:hover {
            background: var(--secondary-dark);
            transform: translateY(-2px);
          }

          .result-panel {
            background: var(--bg-elevated);
            border: 2px solid var(--secondary);
            border-radius: 12px;
            padding: 20px;
            text-align: center;
            max-width: 350px;
          }

          .result-panel h4 {
            margin: 0 0 16px 0;
            color: var(--secondary);
          }

          .color-display {
            display: flex;
            align-items: center;
            gap: 16px;
            justify-content: center;
            margin-bottom: 16px;
          }

          .color-swatch {
            width: 60px;
            height: 60px;
            border-radius: 8px;
            border: 2px solid var(--border);
          }

          .color-info {
            text-align: left;
          }

          .color-name {
            display: block;
            font-weight: 600;
            font-size: 14px;
          }

          .formula {
            display: block;
            font-size: 12px;
            color: var(--text-secondary);
            margin-top: 4px;
          }

          .result-panel .note {
            margin: 0;
            font-size: 12px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .info-box {
            background: var(--bg-elevated);
            border-left: 3px solid var(--primary);
            border-radius: 8px;
            padding: 16px;
            max-width: 300px;
          }

          .info-box h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--primary);
          }

          .info-box ul {
            margin: 0;
            padding-left: 16px;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .info-box li {
            margin: 4px 0;
          }
          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp11"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
